import type {
  PullRequestListResponse,
  ListPullRequestsOptions,
  PullRequestStats,
  PullRequestStatsOptions,
} from '@codemerlin/shared/types/pull-request';
import { api } from '@/lib/api';

function appendFilterParams(queryParams: URLSearchParams, options: PullRequestStatsOptions): void {
  if (options.repository_id) queryParams.append('repository_id', options.repository_id);
  if (options.repository_ids?.length) {
    queryParams.append('repository_ids', options.repository_ids.join(','));
  }
  if (options.team_id) queryParams.append('team_id', options.team_id);
  if (options.author) queryParams.append('author', options.author);
  if (options.reviewed_after) queryParams.append('reviewed_after', options.reviewed_after);
  if (options.reviewed_before) queryParams.append('reviewed_before', options.reviewed_before);
  if (options.score_min !== undefined)
    queryParams.append('score_min', options.score_min.toString());
  if (options.score_max !== undefined)
    queryParams.append('score_max', options.score_max.toString());
  if (options.search) queryParams.append('search', options.search);
  if (options.my_work_scope) queryParams.append('my_work_scope', options.my_work_scope);
}

export const pullRequestService = {
  /**
   * Fetches a paginated list of pull requests with their latest review data
   * @param organizationId - The organization ID to filter by
   * @param options - Query options including filters, sort, pagination
   * @returns Promise resolving to paginated pull request list response
   */
  async listPullRequests(
    organizationId: string,
    options: ListPullRequestsOptions
  ): Promise<PullRequestListResponse> {
    const queryParams = new URLSearchParams();
    appendFilterParams(queryParams, options);
    if (options.sort) queryParams.append('sort', options.sort);
    if (options.limit) queryParams.append('limit', options.limit.toString());
    if (options.cursor) queryParams.append('cursor', options.cursor);

    return api.get<PullRequestListResponse>(
      `organizations/${organizationId}/pull-requests?${queryParams.toString()}`
    );
  },

  /**
   * Fetches PR summary-card stats (total / reviewed / avg score / findings) over
   * the full filtered dataset, decoupled from list pagination.
   * @param organizationId - The organization ID to filter by
   * @param options - Dataset filters (no sort/limit/cursor)
   * @returns Promise resolving to aggregate pull request stats
   */
  async getPullRequestStats(
    organizationId: string,
    options: PullRequestStatsOptions
  ): Promise<PullRequestStats> {
    const queryParams = new URLSearchParams();
    appendFilterParams(queryParams, options);

    return api.get<PullRequestStats>(
      `organizations/${organizationId}/pull-requests/stats?${queryParams.toString()}`
    );
  },
};
